import {
  buildClozeItem,
  buildTextItem,
} from "@/components/storyboard/quiz/review/buildReviewItem";
import {
  isResolved,
  type ValidationSubmission,
} from "@/lib/validationSubmission";
import {
  InteractiveElementStatusKind,
  type InteractiveElementStatus,
} from "@/lib/interactiveElementState";
import type { QuizFreeTextState } from "@/components/storyboard/quiz/QuizFreetext";
import type { QuizGapwordFreeTextState } from "@/components/storyboard/quiz/QuizGapwordFreeText";
import type {
  QuizFreeTextEntryData,
  QuizGapwordFreeTextEntryData,
} from "@/types/storyboard";

/**
 * Card status of the LLM-graded text quizzes (FREETEXT, CLOZE_FREETEXT), read
 * off the learner's saved submissions. The server keeps the answers and
 * verdicts, so a reload — or another device — meets the quiz where it was left
 * instead of consulting the browser.
 *
 * An entry is settled once it is right or has no attempt left (`isResolved`);
 * until then the learner may still work on it.
 */

/** How the answers of a multi-gap entry travel to the server, and back. */
export const encodeClozeFreetextAnswers = (answers: string[]): string =>
  answers.length === 1
    ? answers[0]
    : answers.map((answer, i) => `Lücke ${i + 1}: ${answer}`).join("\n");

export const decodeClozeFreetextAnswers = (
  userInput: string,
  gapCount: number
): string[] => {
  if (gapCount <= 1) return [userInput];
  return userInput.split("\n").map((line) => line.replace(/^Lücke \d+: ?/, ""));
};

const COMPLETED_AT_UNKNOWN = "";

export const resolveFreetextStatus = (
  submission: ValidationSubmission | undefined,
  data: QuizFreeTextEntryData
): InteractiveElementStatus | undefined => {
  if (!submission) return undefined;

  if (isResolved(submission)) {
    const isCorrect = submission.correct === true;
    return {
      kind: InteractiveElementStatusKind.Completed,
      score: { correct: isCorrect ? 1 : 0, total: 1 },
      review: {
        items: [
          buildTextItem({
            prompt: data.instruction,
            userAnswer: submission.userInput,
            isCorrect,
            feedback: submission.feedback,
          }),
        ],
      },
      attempts: 1,
      completedAt: COMPLETED_AT_UNKNOWN,
    };
  }

  return {
    kind: InteractiveElementStatusKind.InProgress,
    partial: {
      userAnswer: submission.userInput,
      isSubmitted: true,
      isCorrect: submission.correct,
      grade: submission.grade,
      feedback: submission.feedback,
      attemptCount: submission.attemptCount,
      maxAttempts: submission.maxAttempts,
    } satisfies QuizFreeTextState,
    lastTaskIndex: 0,
  };
};

export const resolveClozeFreetextStatus = (
  submissions: Record<number, ValidationSubmission>,
  entries: QuizGapwordFreeTextEntryData[]
): InteractiveElementStatus | undefined => {
  if (Object.keys(submissions).length === 0) return undefined;

  const settled = entries.map((_, i) => {
    const submission = submissions[i];
    return submission != null && isResolved(submission);
  });
  const correctCount = entries.filter(
    (_, i) => settled[i] && submissions[i].correct === true
  ).length;

  if (settled.every(Boolean)) {
    return {
      kind: InteractiveElementStatusKind.Completed,
      score: { correct: correctCount, total: entries.length },
      review: {
        items: entries.map((entry, i) => {
          const submission = submissions[i];
          const isCorrect = submission.correct === true;
          const answers = decodeClozeFreetextAnswers(
            submission.userInput,
            entry.gaps.length
          );
          return buildClozeItem({
            prompt: entry.instruction,
            clozeText: entry.clozeText,
            gapMark: entry.gapMark,
            gaps: entry.gaps.map((gap, gapIndex) => ({
              userAnswer: answers[gapIndex] ?? "",
              correctAnswer: gap.referenceAnswer.join(" / "),
              correct: isCorrect,
            })),
            isCorrect,
            // The individual LLM feedback wins over the storyboard's own.
            feedback: submission.feedback.trim()
              ? submission.feedback
              : entry.userFeedback,
          });
        }),
      },
      attempts: 1,
      completedAt: COMPLETED_AT_UNKNOWN,
    };
  }

  // Reopen at the first entry that can still be worked on; the server enforces
  // what is left of its budget.
  const currentIndex = settled.indexOf(false);
  const attemptCounts: Record<number, number> = {};
  Object.entries(submissions).forEach(([index, submission]) => {
    if (submission.attemptCount != null) {
      attemptCounts[Number(index)] = submission.attemptCount;
    }
  });
  // Every entry of a quiz shares its type's cap, so any saved submission can
  // tell the budget of an entry that has not been answered yet.
  const maxAttempts =
    Object.values(submissions).find(
      (submission) => submission.maxAttempts !== undefined
    )?.maxAttempts ?? null;

  return {
    kind: InteractiveElementStatusKind.InProgress,
    partial: {
      currentIndex,
      userAnswers: [],
      isSubmitted: false,
      isCorrect: null,
      correctCount,
      gapCorrect: null,
      attemptCounts,
      maxAttempts,
    } satisfies QuizGapwordFreeTextState,
    lastTaskIndex: currentIndex,
  };
};
